import type { createNativeInstance } from '@transifex/native';
import { useEffect, useState } from 'react';

import {
  useSystemLocale,
  getTransifexLocale,
  toSupportedLanguageCode,
} from '@/modules/localization';

type TxInstance = ReturnType<typeof createNativeInstance>;

/**
 * Sync a Transifex instance to the system locale, reporting when it's safe to render.
 *
 * Takes the instance rather than using the process-wide default `tx`: the app's
 * strings live on `onboardingTx` now (see `../onboardingTx`), so that a host's
 * `tx.init` can't replace the token ours were fetched under.
 *
 * For English (95% of users): ready immediately — no delay, no blank screen.
 * For Spanish (5%): wait for setCurrentLocale to resolve before rendering.
 * This prevents the race where TRANSLATIONS_FETCHED fires before useT()
 * subscriptions are set up (useEffect runs after render), which left some
 * components stuck in English even with a warm Transifex cache.
 *
 * This gate is a deliberate departure from `@vidahealth/login`, whose `LoginRoot`
 * syncs the locale in an effect and accepts a flash of source strings. The race above
 * is the reason; keeping it costs a caller nothing but the `ready` branch.
 *
 * **The gate is one-way: `ready` goes false → true and never back.** The race above is a
 * mount-time one — `useT` subscribes in a passive effect, so during the first render nobody is
 * listening — and after that the subtree is mounted and `@transifex/react` re-renders it from
 * `LOCALE_CHANGED` / `TRANSLATIONS_FETCHED` itself. So there is nothing left to gate, and
 * re-closing on a later `locale` change would be destructive rather than merely cautious:
 * `OnboardingRoot` swaps `children` for `fallback` while the gate is shut, unmounting
 * `RulesEngineProvider` along with the member's in-progress `draft` and `stepHistory`.
 */
export const useLanguageSetting = (instance: TxInstance, localeOverride?: string) => {
  const systemLocale = useSystemLocale();

  // An explicit locale wins over the device's. A host passes one so onboarding follows the
  // language the rest of its UI is showing, which is not always the device's —
  // VidaReactNative lets a member choose one in-app. Normalized through
  // `getTransifexLocale` either way, so a caller can pass a system locale (`es-MX`) and
  // does not need to know our CDS locale codes.
  const transifexLocale = getTransifexLocale(
    localeOverride ? toSupportedLanguageCode(localeOverride) : systemLocale
  );

  const [ready, setReady] = useState(() => {
    if (transifexLocale === 'en') return true;
    return transifexLocale === instance.getCurrentLocale();
  });

  useEffect(() => {
    let cancelled = false;

    // English is our SOURCE language, so there is nothing to fetch — but there may be
    // something to undo, which is why this no longer just returns.
    //
    // `setCurrentLocale('')` is how `@transifex/native` reverts to source: the falsy argument
    // takes its `if (!localeCode)` branch, which clears `currentLocale` and emits
    // `LOCALE_CHANGED` with no network call. Passing `'en'` would instead `await
    // fetchTranslations('en')` — a CDS round-trip for a locale the project need not publish,
    // and one that rejects if it doesn't.
    //
    // At mount this is inert: `currentLocale` starts `''`, so `isCurrent('')` is true and the
    // call short-circuits without emitting. It matters on the way BACK. A host moving es → en
    // used to hit an early `return` here, leaving the instance on `es_US` — every string
    // rendered Spanish underneath an English UI, with nothing to indicate why.
    if (transifexLocale === 'en') {
      instance.setCurrentLocale('').catch((error: unknown) => {
        console.warn('[useLanguageSetting] could not revert to source strings', error);
      });
      setReady(true);
      return;
    }

    if (transifexLocale === instance.getCurrentLocale()) {
      setReady(true);
      return;
    }

    // NOTE: no `setReady(false)` here, deliberately — see the one-way note above. The initial
    // `useState` already computed `false` for the only render where `false` is correct, so
    // this is the sole place it could ever be set again, and every caller of it would be a
    // LATER locale change closing a gate that is already open.
    //
    // Resolve the gate on failure as well as on success. `setCurrentLocale` awaits
    // `fetchTranslations`, which REJECTS on any non-2xx — an offline device, a CDS outage, or
    // an instance pointed at an empty token because the caller passed no `transifexToken`.
    // Leaving `ready` false on that path holds `OnboardingRoot`'s children back forever, so a
    // failed fetch would render a permanently blank app rather than the source strings. Source
    // English is the documented degradation; a blank screen is not.
    const finish = () => {
      if (!cancelled) setReady(true);
    };
    instance.setCurrentLocale(transifexLocale).then(finish, (error: unknown) => {
      console.warn(
        `[useLanguageSetting] could not load translations for ${transifexLocale} — rendering source strings`,
        error
      );
      finish();
    });

    return () => {
      cancelled = true;
    };
  }, [transifexLocale, instance]);

  return { ready };
};
